import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Button, { type ButtonProps } from "components/Buttons/Button";
import { pxToRem } from "styles/utils";

export interface CopyFieldProps {
  value: string;
  ariaLabel?: string;
  buttonLabel?: string;
  copiedLabel?: string;
  buttonVariant?: ButtonProps["variant"];
  disabled?: boolean;
  onCopyError?: (error: unknown) => void;
}

const Root = styled.div`
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  gap: ${pxToRem(10)};
  min-width: 0;
`;

const Field = styled.input`
  box-sizing: border-box;
  min-width: 0;
  flex: 1 1 auto;
  height: ${pxToRem(50)};
  padding: 0 ${pxToRem(14)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${pxToRem(17.5)};
  text-overflow: ellipsis;

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(-1)};
  }
`;

const ActionSlot = styled.div`
  flex: 0 0 auto;

  > button {
    width: auto;
    min-width: 0;
    max-width: none;
  }
`;

const Status = styled.div<{ $visible: boolean }>`
  height: ${pxToRem(20)};
  margin-top: ${pxToRem(8)};
  color: ${({ theme }) => theme.colors.success};
  font-size: ${pxToRem(15)};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

const fallbackCopy = (value: string) => {
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("Clipboard API is unavailable");
  }
};

const CopyField: React.FC<CopyFieldProps> = ({
  value,
  ariaLabel = "Ссылка для копирования",
  buttonLabel = "Скопировать",
  copiedLabel = "Ссылка скопирована",
  buttonVariant = "primary",
  disabled = false,
  onCopyError,
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        fallbackCopy(value);
      }
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      onCopyError?.(error);
    }
  };

  return (
    <Root>
      <Row>
        <Field value={value} readOnly aria-label={ariaLabel} />
        <ActionSlot>
          <Button
            variant={buttonVariant}
            size="md"
            onClick={handleCopy}
            disabled={disabled || !value}
          >
            {buttonLabel}
          </Button>
        </ActionSlot>
      </Row>
      <Status $visible={copied} aria-live="polite">
        {copied ? copiedLabel : ""}
      </Status>
    </Root>
  );
};

export default CopyField;
